import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpContext, HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import {environment} from "../../../environments/environment";
import {HttpRequestService} from './http-request.service';
import {countrySample, phoneCountrySample, postcodeSample, stateSample} from '../data/default.data';
import GlobalComponent from '../../global-component';
import {RETRY_ON_AUTH_FAILURE} from './authentication/auth.interceptor';
import {AuthService} from './authentication/auth.service';
import {AuthHelper} from '../helpers/auth.helper';


@Injectable({providedIn: 'root'})
export class MerchantService {
  // Inject services
  private http = inject(HttpClient);
  private httpRequestService = inject(HttpRequestService);
  private auth = inject(AuthService);
  private authHelper = inject(AuthHelper);
  readonly VALIDATE_URL: string = 'validate';

  /**
   * Fetch dropdown data for multiple modules.
   */
  async validate(
    token: string,
    clientId: string,
    options?: { context?: HttpContext; retryOnAuthFailure?: boolean }
  ): Promise<any> {
    if (!token) {
      throw new Error('Missing token.');
    }

    // Build query parameters
    const queryParams = new URLSearchParams();
    // Convert `modules` object to query string
    const fullUrl = `${GlobalComponent.VALIDATE_TOKEN_URL}?${queryParams.toString()}`;

    // Retry once on auth failure to coalesce transient issues during refresh
    const context = new HttpContext().set(RETRY_ON_AUTH_FAILURE, true);

    // Grab in-memory access token (fetched earlier by getToken)
    const accessToken = this.auth.getAccessToken() ?? '';
    if (!accessToken) {
      // You can throw or silently proceed—depending on your auth flow.
      // Throwing makes the failure explicit:
      throw new Error('Missing access token.');
    }

    // Construct headers
    let headers = await this.authHelper.buildSignedHeaders();

    const payload = {
      token: token,
      client_id: clientId
    };

    return this.httpRequestService.executeRequest(
      fullUrl,
      headers,
      "post",
      payload ?? {},
      undefined,
      context,
      true
    );
  }

  /**
   * Submit Form data
   */
  async submitRegistration(
    payload: Record<string, unknown>,
    options?: { context?: HttpContext; retryOnAuthFailure?: boolean }
  ): Promise<any> {
    // Build query parameters
    const queryParams = new URLSearchParams();
    const fullUrl = `${GlobalComponent.REGISTRATION_URL}?${queryParams.toString()}`;

    // Retry once on auth failure to coalesce transient issues during refresh
    const context = new HttpContext().set(RETRY_ON_AUTH_FAILURE, true);

    // Grab in-memory access token (fetched earlier by getToken)
    const accessToken = this.auth.getAccessToken() ?? '';
    if (!accessToken) {
      // You can throw or silently proceed—depending on your auth flow.
      // Throwing makes the failure explicit:
      throw new Error('Missing access token.');
    }

    // Construct headers
    let headers = await this.authHelper.buildSignedHeaders();

    return this.httpRequestService.executeRequest(
      fullUrl,
      headers,
      "post",
      payload ?? {},
      undefined,
      context,
      true
    );
  }

  async submitOnboardingWithFile(
    payload: FormData | Record<string, unknown>,
    options?: { context?: HttpContext; retryOnAuthFailure?: boolean; responseType?: 'json' | 'text' }
  ): Promise<any> {
    // Build query parameters
    const queryParams = new URLSearchParams();
    const fullUrl = `${GlobalComponent.SUBMIT_URL}?${queryParams.toString()}`;
    // Grab in-memory access token (fetched earlier by getToken)
    const token = this.auth.getAccessToken() ?? '';
    if (!token) {
      // You can throw or silently proceed—depending on your auth flow.
      // Throwing makes the failure explicit:
      throw new Error('Missing access token.');
    }

    // Construct headers
    let headers = await this.authHelper.buildSignedHeaders(true);

    // ⚠️ Critical for multipart: remove any JSON content-type so the browser sets the boundary
    const isFormData = typeof FormData !== 'undefined' && payload instanceof FormData;
    if (isFormData && (headers as any).has?.('Content-Type')) {
      headers = (headers as any).delete('Content-Type');
    }
    console.log('headers:', headers);

    // Retry once on auth failure to coalesce transient issues during refresh
    const context = new HttpContext().set(RETRY_ON_AUTH_FAILURE, true);

    return this.httpRequestService.executeRequest(
      fullUrl,
      headers,
      "post",
      undefined,
      payload,
      context,
      true
    );
  }

  async submitOnboarding
  (
    payload: FormData | Record<string, unknown>,
    options?: { context?: HttpContext; retryOnAuthFailure?: boolean; responseType?: 'json' | 'text' }
  ): Promise<any> {
    // Build query parameters
    const queryParams = new URLSearchParams();
    const fullUrl = `${GlobalComponent.SUBMIT_OLD_URL}?${queryParams.toString()}`;
    // Grab in-memory access token (fetched earlier by getToken)
    const token = this.auth.getAccessToken() ?? '';
    if (!token) {
      // You can throw or silently proceed—depending on your auth flow.
      // Throwing makes the failure explicit:
      throw new Error('Missing access token.');
    }

    // Construct headers
    let headers = await this.authHelper.buildSignedHeaders();

    // ⚠️ Critical for multipart: remove any JSON content-type so the browser sets the boundary
    const isFormData = typeof FormData !== 'undefined' && payload instanceof FormData;
    if (isFormData && (headers as any).has?.('Content-Type')) {
      headers = (headers as any).delete('Content-Type');
    }

    // Retry once on auth failure to coalesce transient issues during refresh
    const context = new HttpContext().set(RETRY_ON_AUTH_FAILURE, true);

    return this.httpRequestService.executeRequest(
      fullUrl,
      headers,
      "post",
      payload,
      undefined,
      context,
      true
    );
  }
}
